import React, { useState } from 'react';
import SimpleLayout from '@/layouts/SimpleLayout';
import MaterialIcon from '@/components/MaterialIcon';
import SmartLink from '@/components/SmartLink';
import { useTracking } from '@mygpt/app/utils';

/**
 * Contact Page
 * お問い合わせページ - GBaseSupportSLPのcontact.htmlに基づく
 *
 * 機能:
 * - Web3Forms APIを使用したフォーム送信
 * - Solution色系のデザイン
 * - レスポンシブ対応
 * - 送信後のCTAセクション
 */

interface FormData {
  inquiry_type: string;
  name: string;
  company: string;
  department: string;
  position: string;
  email: string;
  phone: string;
  message: string;
  privacy_agreed: boolean;
}

const ContactPage: React.FC = () => {
  const { trackContactClick, trackCustomEvent } = useTracking();

  const [formData, setFormData] = useState<FormData>({
    inquiry_type: '無料デモ',
    name: '',
    company: '',
    department: '',
    position: '',
    email: '',
    phone: '',
    message: '',
    privacy_agreed: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('access_key', '937807c3-360b-45db-ae1e-e314dce87234');
      formDataToSend.append('subject', 'GBase Support - 新規お問い合わせ');
      formDataToSend.append('from_name', 'GBase Support Website');
      formDataToSend.append('inquiry_type', formData.inquiry_type);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('department', formData.department);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: 'お問い合わせありがとうございます。送信が完了しました。',
        });
        trackCustomEvent('contact_submit_success', {
          event_category: 'form_submit',
          event_label: 'contact_form',
        });
        // Reset form
        setFormData({
          inquiry_type: '無料デモ',
          name: '',
          company: '',
          department: '',
          position: '',
          email: '',
          phone: '',
          message: '',
          privacy_agreed: false,
        });
      } else {
        throw new Error(result.message || '送信に失敗しました');
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'エラーが発生しました。しばらく経ってから再度お試しください。',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SimpleLayout>
      <main className="flex flex-col items-center w-full">
        {/* Hero Section */}
        <div className="w-full px-4 md:px-10 lg:px-40 py-12 md:py-16 bg-gradient-to-br from-blue-500/5 to-grape-500/5">
          <div className="max-w-[1200px] w-full mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-grape-500/10 text-grape-600 text-sm font-bold mb-6">
              <MaterialIcon name="mail" className="text-lg" />
              Contact Us
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-solution-text mb-4">お問い合わせ</h1>
            <p className="text-lg text-solution-text-secondary max-w-2xl mx-auto">
              ご質問・ご相談はこちらからお気軽にどうぞ
            </p>
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="w-full px-4 md:px-10 lg:px-40 py-16">
          <div className="max-w-[800px] mx-auto">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-6 md:p-10 border border-solution-border shadow-sm"
            >
              {/* Inquiry Type */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-solution-text mb-3">
                  お問い合わせ種別 <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-4">
                  {['無料デモ', '資料請求', '導入相談', 'その他'].map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="inquiry_type"
                        value={type}
                        checked={formData.inquiry_type === type}
                        onChange={handleChange}
                        className="w-4 h-4 text-solution-orange"
                      />
                      <span className="text-solution-text">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Name & Company Row */}
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="例：山田太郎"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-solution-border bg-white text-solution-text placeholder:text-gray-400 focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="例：株式会社GBase"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-solution-border bg-white text-solution-text placeholder:text-gray-400 focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all"
                  />
                </div>
              </div>

              {/* Department & Position Row */}
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">部署</label>
                  <input
                    type="text"
                    name="department"
                    placeholder="例：経営企画室"
                    value={formData.department}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-solution-border bg-white text-solution-text placeholder:text-gray-400 focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">役職</label>
                  <input
                    type="text"
                    name="position"
                    placeholder="例：部長"
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-solution-border bg-white text-solution-text placeholder:text-gray-400 focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all"
                  />
                </div>
              </div>

              {/* Email & Phone Row */}
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="例：yamada@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-solution-border bg-white text-solution-text placeholder:text-gray-400 focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">電話番号</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="例：03-1234-5678"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-solution-border bg-white text-solution-text placeholder:text-gray-400 focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-solution-text mb-2">お問い合わせ内容</label>
                <textarea
                  name="message"
                  rows={4}
                  placeholder="ご質問やご要望をお書きください"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-solution-border bg-white text-solution-text placeholder:text-gray-400 focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all resize-none"
                />
              </div>

              {/* Privacy Policy */}
              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="privacy_agreed"
                    required
                    checked={formData.privacy_agreed}
                    onChange={handleChange}
                    className="w-4 h-4 mt-1 text-solution-orange rounded"
                  />
                  <span className="text-sm text-solution-text-secondary">
                    <a
                      href="https://gbase.ai/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-solution-orange hover:underline"
                    >
                      プライバシーポリシー
                    </a>
                    に同意します
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 rounded-lg h-14 px-8 bg-solution-orange hover:bg-solution-orange-dark transition-all text-white text-lg font-bold shadow-lg shadow-solution-orange/20 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => trackContactClick('contact_form')}
              >
                <MaterialIcon name="send" />
                <span>{isSubmitting ? '送信中...' : '送信する'}</span>
              </button>

              {/* Success/Error Messages */}
              {submitStatus.type && (
                <div
                  className={`mt-4 p-4 rounded-lg text-center ${
                    submitStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}
            </form>

            <p className="text-xs text-solution-text-secondary text-center mt-4">
              ※営業目的のお問い合わせはお断りしております
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="w-full px-4 md:px-10 lg:px-40 py-16 bg-solution-bg-dark text-white">
          <div className="max-w-[800px] mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">まずは無料で試してみませんか？</h2>
            <p className="text-gray-300 mb-8">14日間の無料トライアルで、GBase Supportの効果を実感してください。</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SmartLink
                to="/register"
                className="flex items-center justify-center rounded-lg h-12 px-8 bg-solution-orange hover:bg-solution-orange-dark transition-colors text-white text-base font-bold"
              >
                14日間無料トライアル開始
              </SmartLink>
              <SmartLink
                href="https://prtimes.jp/main/html/rd/p/000000215.000003279.html"
                target="_blank"
                className="flex items-center justify-center gap-2 rounded-lg h-12 px-8 bg-transparent border border-gray-600 hover:border-white transition-colors text-white text-base font-bold"
              >
                <MaterialIcon name="open_in_new" className="text-lg" />
                導入事例を見る
              </SmartLink>
            </div>
          </div>
        </div>
      </main>
    </SimpleLayout>
  );
};

export default ContactPage;
